/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React, { useState, useRef, useEffect } from 'react';
import { UploadIcon } from './icons';
import { Folder, Plus, Settings, Database, ChevronDown, ChevronRight, MoreHorizontal, Edit2, Trash2, Sparkles, Wand2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface StartScreenProps {
  onFileSelect: (files: FileList | null) => void;
  onPromptSubmit: (prompt: string, model: 'fast' | 'detailed') => void;
  isLoading?: boolean;
  currentProjectName?: string;
  hasAnyProjects: boolean;
  projects: Project[];
  onThreadCreate: (projectId: string) => void;
  onProjectSettings: (projectId: string) => void;
  hasAccessToBulkTreatments: boolean;
  onThreadSelect: (projectId: string, threadId: string) => void;
  onThreadEdit: (projectId: string, threadId: string, newTitle: string) => void;
  onThreadDelete: (projectId: string, threadId: string) => void;
}

const StartScreen: React.FC<StartScreenProps> = ({ 
  onFileSelect, 
  onPromptSubmit,
  currentProjectName, 
  isLoading = false, 
  hasAnyProjects,
  projects,
  onThreadCreate,
  onProjectSettings,
  hasAccessToBulkTreatments,
  onThreadSelect,
  onThreadEdit,
  onThreadDelete
}) => {
  const navigate = useNavigate();
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [editingThread, setEditingThread] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [showPromptInput, setShowPromptInput] = useState(false);
  const [generationPrompt, setGenerationPrompt] = useState('');
  const [generationModel, setGenerationModel] = useState<'fast' | 'detailed'>('fast');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isUploadDisabled = isLoading || !currentProjectName;
  const isPromptDisabled = isLoading || !currentProjectName;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isUploadDisabled) return;
    onFileSelect(e.target.files);
  };

  const preventIfLoading = (e: React.SyntheticEvent) => {
    if (isUploadDisabled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handlePromptSubmit = () => {
    if (isPromptDisabled || !generationPrompt.trim()) return;
    onPromptSubmit(generationPrompt.trim(), generationModel);
    setGenerationPrompt('');
    setShowPromptInput(false);
  };

  const toggleProject = (projectId: string) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleEditClick = (threadId: string, currentTitle: string) => {
    setEditingThread(threadId);
    setEditTitle(currentTitle);
    setActiveDropdown(null);
  };

  const handleEditSubmit = (projectId: string, threadId: string) => {
    if (editTitle.trim()) {
      onThreadEdit(projectId, threadId, editTitle.trim());
    }
    setEditingThread(null);
    setEditTitle('');
  };

  const handleEditCancel = () => {
    setEditingThread(null);
    setEditTitle('');
  };

  const handleDeleteClick = (projectId: string, threadId: string) => {
    onThreadDelete(projectId, threadId);
    setActiveDropdown(null);
  };

  // Show message when user has NO projects at all
  if (!hasAnyProjects) {
    return (
      <div className="w-full max-w-3xl mx-auto text-center p-8 animate-fade-in">
        <div className="flex flex-col items-center gap-6">
          <h1 className="text-4xl font-extrabold tracking-tight text-gray-100 sm:text-5xl">
            Welcome to AI Conic
          </h1>
          <p className="max-w-xl text-lg text-gray-400">
            You currently don't have access to any projects.
          </p>
          <div className="mt-4 bg-blue-500/10 border border-blue-500/30 rounded-xl p-6 max-w-xl">
            <p className="text-base text-gray-300">
              To get started, please contact our team to set up your account and gain access to projects:
            </p>
            <a 
              href="mailto:monfort@labelium.com" 
              className="mt-3 inline-block text-blue-400 hover:text-blue-300 font-semibold text-lg underline transition-colors"
            >
              monfort@labelium.com
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full max-w-7xl mx-auto p-8 animate-fade-in">
      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full">
        
        {/* LEFT COLUMN - Upload Section */}
        <div 
          className={[
            "flex flex-col items-center justify-center p-8 rounded-2xl border-2 transition-all duration-300",
            isDraggingOver 
              ? "bg-blue-500/10 border-dashed border-blue-400" 
              : "bg-gray-800/40 border-gray-700/50"
          ].join(" ")}
          onDragOver={(e) => {
            e.preventDefault();
            if (isUploadDisabled) return;
            setIsDraggingOver(true);
          }}
          onDragLeave={() => {
            if (isUploadDisabled) return;
            setIsDraggingOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (isUploadDisabled) return;
            setIsDraggingOver(false);
            onFileSelect(e.dataTransfer.files);
          }}
        >
          <div className="text-center max-w-md w-full">
            <h2 className="text-3xl font-bold text-gray-100 mb-2">
              Create Your Image
            </h2>
            <p className="text-gray-400 mb-8">
              AI-powered photo editing made simple
            </p>

            {/* Current Project Badge - Improved Layout */}
            {currentProjectName ? (
              <div className="mb-6">
                <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide">Current Project</p>
                <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-lg px-5 py-3">
                  <Folder className="w-5 h-5 text-blue-400" />
                  <span className="font-semibold text-blue-400 text-lg">{currentProjectName}</span>
                </div>
              </div>
            ) : (
              <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-sm text-yellow-200">
                  👉 Select a project on the right to start
                </p>
              </div>
            )}

            {/* Upload Area - Hidden when prompt input is shown */}
            {!showPromptInput && (
              <>
                <label
                  htmlFor="image-upload-start"
                  className={[
                    "relative flex flex-col items-center justify-center w-full py-8 px-4 border-2 border-dashed rounded-xl transition-all duration-200 mb-4",
                    isUploadDisabled
                      ? "bg-gray-700/50 border-gray-600 cursor-not-allowed"
                      : "bg-gray-800/50 border-gray-600 hover:border-blue-400 hover:bg-blue-500/10 cursor-pointer"
                  ].join(" ")}
                  onClick={preventIfLoading}
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="w-8 h-8 mb-2 animate-spin text-blue-400"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" opacity="0.25" />
                        <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                      </svg>
                      <span className="text-gray-300">Working…</span>
                    </>
                  ) : (
                    <>
                      <UploadIcon className="w-10 h-10 mb-2 text-blue-400" />
                      <span className="text-gray-300 font-medium">Click to browse or drag & drop</span>
                      <span className="text-sm text-gray-500 mt-1">PNG, JPG, WebP supported</span>
                    </>
                  )}
                </label>

                <input
                  id="image-upload-start"
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={isUploadDisabled}
                />

                {/* Divider */}
                <div className="flex items-center gap-3 text-gray-500 mb-4">
                  <div className="flex-1 h-px bg-gray-700"></div>
                  <span className="text-sm">or</span>
                  <div className="flex-1 h-px bg-gray-700"></div>
                </div>
              </>
            )}

            {/* Generate with AI - Requires a click to reveal */}
            {!showPromptInput ? (
              <button
                onClick={() => !isPromptDisabled && setShowPromptInput(true)}
                className={[
                  "w-full flex items-center justify-center gap-3 px-6 py-4 text-lg font-bold rounded-lg transition-all duration-200",
                  isPromptDisabled
                    ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                    : "text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 cursor-pointer"
                ].join(" ")}
                title={
                  !currentProjectName 
                    ? "Please select a project first" 
                    : isLoading 
                    ? "Processing in progress" 
                    : "Generate an image with AI"
                }
              >
                <Sparkles className="w-5 h-5" />
                Generate with AI
              </button>
            ) : (
              <div className="space-y-4 text-left overflow-y-auto max-h-[60vh]">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-200 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-400" />
                    Generate with AI
                  </h3>
                  <button
                    onClick={() => {
                      setShowPromptInput(false);
                      setGenerationPrompt('');
                    }}
                    className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Go back"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>

                {/* Model Selection */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setGenerationModel('fast')}
                    disabled={isLoading}
                    className={[
                      "flex-1 flex flex-col items-center gap-1 px-4 py-3 rounded-lg border-2 transition-all duration-200",
                      generationModel === 'fast'
                        ? "border-blue-500 bg-blue-500/20 text-white"
                        : "border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600 hover:text-gray-300"
                    ].join(" ")}
                  >
                    <span className="text-lg">⚡</span>
                    <span className="font-semibold text-sm">Fast</span>
                    <span className="text-xs opacity-70">Quick results</span>
                  </button>
                  <button
                    onClick={() => setGenerationModel('detailed')}
                    disabled={isLoading}
                    className={[
                      "flex-1 flex flex-col items-center gap-1 px-4 py-3 rounded-lg border-2 transition-all duration-200",
                      generationModel === 'detailed'
                        ? "border-purple-500 bg-purple-500/20 text-white"
                        : "border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600 hover:text-gray-300"
                    ].join(" ")}
                  >
                    <span className="text-lg">✨</span>
                    <span className="font-semibold text-sm">Detailed</span>
                    <span className="text-xs opacity-70">Higher quality</span>
                  </button>
                </div>

                <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 backdrop-blur-xl border border-purple-500/20 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Wand2 className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-200 mb-1">Describe your image</p>
                      <p className="text-xs text-gray-400 leading-relaxed">
                        Be specific about style, colors, composition, and details you want in your generated image.
                      </p>
                    </div>
                  </div>
                </div>

                <textarea
                  value={generationPrompt}
                  onChange={(e) => setGenerationPrompt(e.target.value)}
                  placeholder="E.g., A modern product photo of a sleek white headphone on a gradient purple background with soft lighting..."
                  className="w-full bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 text-gray-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 focus:outline-none transition disabled:cursor-not-allowed disabled:opacity-60 resize-none placeholder:text-gray-500"
                  disabled={isLoading}
                  rows={4}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.metaKey) {
                      e.preventDefault();
                      handlePromptSubmit();
                    }
                  }}
                />

                <button
                  onClick={handlePromptSubmit}
                  disabled={isLoading || !generationPrompt.trim()}
                  className="w-full bg-gradient-to-r from-purple-600 via-purple-500 to-blue-600 text-white font-semibold py-3 px-4 text-sm rounded-lg transition-all duration-300 ease-out shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/40 hover:scale-[1.02] active:scale-[0.98] disabled:from-gray-700 disabled:to-gray-600 disabled:shadow-none disabled:cursor-not-allowed disabled:scale-100 relative overflow-hidden group"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {isLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Generate Image
                      </>
                    )}
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                </button>

                <p className="text-xs text-gray-500 text-center">
                  Press ⌘+Enter to generate
                </p>
              </div>
            )}

            {!showPromptInput && (
              <p className="text-sm text-gray-500 mt-4">
                {isLoading 
                  ? "Hold on while we process your image…" 
                  : currentProjectName
                  ? "Upload an image or generate one with AI"
                  : "You need to select a project first"}
              </p>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN - Projects Browser */}
        <div className="flex flex-col border-2 border-gray-700/50 rounded-2xl bg-gray-800/40 overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-gray-700/50 bg-gray-800/60 flex-shrink-0">
            <h2 className="text-2xl font-bold text-gray-100 flex items-center gap-3">
              <Folder className="w-6 h-6 text-blue-400" />
              Your Projects & Threads
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Select a project to upload images or click a thread to open it
            </p>
          </div>

          {/* Projects List - Scrollable */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {projects.map((project) => {
              const isExpanded = expandedProjects.has(project.project_id);
              
              return (
                <div 
                  key={project.project_id} 
                  className="border-2 border-gray-700/70 rounded-xl bg-gray-800/60 overflow-hidden hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-200 group"
                >
                  <div className="p-5">
                    {/* Project Header */}
                    <div 
                      className="flex items-start gap-4 mb-4 cursor-pointer"
                      onClick={() => toggleProject(project.project_id)}
                    >
                      <div className="flex items-center justify-center w-12 h-12 bg-blue-500/10 rounded-lg flex-shrink-0">
                        <Folder className="w-6 h-6 text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-lg text-gray-200 truncate mb-1">
                          {project.project_name}
                        </h3>
                        <p className="text-xs text-gray-400">
                          {project.threads.length} {project.threads.length === 1 ? 'thread' : 'threads'}
                        </p>
                      </div>
                      <button className="p-1 hover:bg-gray-700/50 rounded transition-colors">
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    
                    {/* Threads List - Collapsible */}
                    {isExpanded && project.threads.length > 0 && (
                      <div className="mb-4 space-y-2 max-h-60 overflow-y-auto pr-2">
                        {project.threads
                          .sort((a, b) => {
                            const timeA = a.updated_at instanceof Date ? a.updated_at.getTime() : 0;
                            const timeB = b.updated_at instanceof Date ? b.updated_at.getTime() : 0;
                            return timeB - timeA;
                          })
                          .map((thread) => {
                            const isEditing = editingThread === thread.thread_id;
                            
                            return (
                            <div
                              key={thread.thread_id}
                              className="relative"
                            >
                              {isEditing ? (
                                <div className="flex items-center gap-2 p-3 bg-gray-900/40 rounded-lg border border-blue-500">
                                  <input
                                    type="text"
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleEditSubmit(project.project_id, thread.thread_id);
                                      } else if (e.key === 'Escape') {
                                        handleEditCancel();
                                      }
                                    }}
                                    className="flex-1 bg-gray-800 text-gray-300 text-sm px-3 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    autoFocus
                                  />
                                </div>
                              ) : (
                                <div className="group/thread flex items-center gap-3 p-3 bg-gray-900/40 hover:bg-gray-900/60 rounded-lg transition-colors border border-gray-700/30 hover:border-blue-500/40">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onThreadSelect(project.project_id, thread.thread_id);
                                    }}
                                    className="flex items-center gap-3 flex-1 min-w-0"
                                  >
                                    {/* Thumbnail */}
                                    <div className="relative w-12 h-12 rounded overflow-hidden bg-gray-950 border border-gray-700 shrink-0">
                                      <img
                                        src={thread.thumbnail_url || '/placeholder-thumb.png'}
                                        alt=""
                                        loading="lazy"
                                        className="w-full h-full object-contain"
                                        onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/placeholder-thumb.png'; }}
                                        draggable={false}
                                      />
                                    </div>
                                    <div className="flex-1 min-w-0 text-left">
                                      <p className="text-sm font-medium text-gray-300 group-hover/thread:text-gray-100 truncate">
                                        {thread.name}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {formatDate(thread.updated_at)}
                                      </p>
                                    </div>
                                  </button>
                                  
                                  {/* Three dots menu - inside the darkened area */}
                                  <div className="relative shrink-0" ref={activeDropdown === thread.thread_id ? dropdownRef : null}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveDropdown(activeDropdown === thread.thread_id ? null : thread.thread_id);
                                      }}
                                      className="opacity-0 group-hover/thread:opacity-100 p-1.5 hover:bg-gray-600 rounded transition-all duration-150"
                                    >
                                      <MoreHorizontal className="w-4 h-4 text-gray-400" />
                                    </button>
                                    
                                    {/* Dropdown menu */}
                                    {activeDropdown === thread.thread_id && (
                                      <div className="absolute right-0 top-8 bg-gray-800 border border-gray-700 rounded-lg shadow-lg py-1 z-50 min-w-[140px]">
                                        <button
                                          onClick={() => handleEditClick(thread.thread_id, thread.name)}
                                          className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700 transition-colors"
                                        >
                                          <Edit2 className="w-4 h-4" />
                                          <span>Edit Name</span>
                                        </button>
                                        <button
                                          onClick={() => handleDeleteClick(project.project_id, thread.thread_id)}
                                          className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/20 transition-colors"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                          <span>Delete Thread</span>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => onThreadCreate(project.project_id)}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors text-sm font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        New Thread
                      </button>
                      {hasAccessToBulkTreatments && (
                        <button
                          onClick={() => navigate(`/projects/${project.project_id}/bulk-treatments`)}
                          className="px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors"
                          title="Bulk Treatments"
                        >
                          <Database className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => onProjectSettings(project.project_id)}
                        className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg transition-colors"
                        title="Project settings"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartScreen;
